import { useState, useEffect } from "react";
import { API_BASE } from "@config/env";
import axios from "axios";
import SectionCard from "./SectionCard";

const post = (ep, f) => {
  const fd = new FormData();
  Object.entries(f).forEach(([k,v]) => fd.append(k,v));
  return axios.post(`${API_BASE}${ep}`, fd).then(r=>r.data).catch(()=>null);
};

const AVATAR_COLORS = ["#2563EB","#7C3AED","#16A34A","#D97706","#DB2777","#0891B2"];
const colorFor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  const sum = [...name].reduce((a,c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
};
const initialsFor = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0]||"") + (parts[1]?.[0]||"")).toUpperCase() || name[0].toUpperCase();
};

export default function RecruiterNotesTab({ resourceSlug, userSlug }) {
  const [notes,   setNotes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [text,    setText]    = useState("");
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");
  const [deletingSlug, setDeletingSlug] = useState(null);
  const [confirmSlug,  setConfirmSlug]  = useState(null);
  const [hoverSlug,    setHoverSlug]    = useState(null);

  const load = () => {
    post("get-resource-details", { resource_slug: resourceSlug, user_slug: userSlug })
      .then(d => {
        const n = d?.resource_details?.recruiter_notes;
        setNotes(Array.isArray(n) ? n : []);
        setLoading(false);
      });
  };

  useEffect(() => { load(); }, [resourceSlug]);

  const addNote = async () => {
    if (!text.trim()) return;
    setSaving(true); setError("");
    const res = await post("create-note", { resource_slug: resourceSlug, r_slug: userSlug, note: text });
    setSaving(false);
    if (res?.status === 1) {
      setText("");
      load();
    } else {
      setError(res?.message || "Unable to save note. Please try again.");
    }
  };

  const deleteNote = async (noteSlug) => {
    setDeletingSlug(noteSlug); setConfirmSlug(null); setError("");
    const res = await post("remove-note", { r_slug: userSlug, note_slug: noteSlug });
    setDeletingSlug(null);
    if (res?.status === 1) {
      load();
    } else {
      setError(res?.message || "Unable to delete note. Please try again.");
    }
  };

  return (
    <SectionCard title="Recruiter Notes">
      {/* Composer */}
      <div style={{ marginBottom: 20 }}>
        {error && (
          <div style={{ padding:"9px 12px", background:"var(--fc-red-light)", border:"1px solid var(--fc-red-border)", borderRadius:9, marginBottom:10, fontSize:12.5, color:"var(--fc-red)" }}>
            {error}
          </div>
        )}
        <textarea value={text} onChange={e=>setText(e.target.value)} rows={2}
          placeholder="Add a note about this candidate..."
          style={{ width:"100%", boxSizing:"border-box", padding:"10px 12px", border:"1.5px solid var(--fc-border)", borderRadius:10, fontSize:13, outline:"none", fontFamily:"inherit", resize:"vertical", background:"var(--fc-card-bg)", color:"var(--fc-text-primary)", transition:"border-color 0.15s" }}
          onFocus={e=>e.target.style.borderColor="#2563EB"} onBlur={e=>e.target.style.borderColor="var(--fc-border)"} />
        <div style={{ display:"flex", justifyContent:"flex-end", marginTop:8 }}>
          <button onClick={addNote} disabled={saving||!text.trim()} className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill">
            {saving ? <><i className="fa-solid fa-spinner fa-spin me-1" style={{fontSize:10}}/>Saving...</> : <><i className="fa-solid fa-plus me-1" style={{fontSize:10}}/>Add Note</>}
          </button>
        </div>
      </div>

      {/* Feed */}
      {loading ? (
        <div style={{ textAlign:"center", color:"var(--fc-text-subtle)", padding:"24px 0" }}>
          <i className="fa-solid fa-spinner fa-spin" />
        </div>
      ) : notes.length === 0 ? (
        <div style={{ textAlign:"center", padding:"28px 16px", color:"var(--fc-text-subtle)" }}>
          <i className="fa-regular fa-note-sticky" style={{ fontSize:22, marginBottom:8, display:"block", color:"var(--fc-text-hint)" }} />
          <div style={{ fontSize:13 }}>No notes yet. Add your first note above.</div>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column" }}>
          {notes.map((n, i) => {
            const isAuthor = n.r_slug && n.r_slug === userSlug;
            const isLast = i === notes.length - 1;
            const isAI = String(n.is_ai_generated) === "1";
            const displayName = isAI ? "Connect IQ" : (n.r_name || "Unknown");
            const color = isAI ? "#7C3AED" : colorFor(n.r_name);
            return (
              <div key={n.note_slug||i}
                onMouseEnter={()=>setHoverSlug(n.note_slug)}
                onMouseLeave={()=>setHoverSlug(null)}
                style={{ display:"flex", gap:12, padding:"14px", background:"var(--fc-bg-light)", borderRadius:10, marginBottom: isLast ? 0 : 8 }}>

                {/* Avatar */}
                <div style={{ width:32, height:32, borderRadius:"50%", background:color+"1E", color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, flexShrink:0 }}>
                  {isAI ? <i className="fa-solid fa-robot" style={{fontSize:13}} /> : initialsFor(n.r_name)}
                </div>

                {/* Body */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"baseline", gap:7, marginBottom:3, flexWrap:"wrap" }}>
                    <span style={{ fontSize:13, fontWeight:700, color:"var(--fc-text-primary)" }}>{displayName}</span>
                    <span style={{ fontSize:11.5, color:"var(--fc-text-subtle)" }}>{n.added_on}</span>
                  </div>
                  <p style={{ fontSize:13.5, color:"var(--fc-text-secondary)", lineHeight:1.6, margin:0, wordBreak:"break-word" }}>
                    {n.note}
                  </p>
                </div>

                {/* Delete (hover-revealed) */}
                {isAuthor && (
                  <div style={{ flexShrink:0, width:22 }}>
                    {confirmSlug === n.note_slug ? (
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:3 }}>
                        <button onClick={()=>deleteNote(n.note_slug)} disabled={deletingSlug===n.note_slug}
                          style={{ background:"none", border:"none", cursor:"pointer", color:"var(--fc-red)", fontSize:11, fontWeight:700, padding:0, whiteSpace:"nowrap" }}>
                          {deletingSlug===n.note_slug ? "..." : "Delete?"}
                        </button>
                        <button onClick={()=>setConfirmSlug(null)}
                          style={{ background:"none", border:"none", cursor:"pointer", color:"var(--fc-text-subtle)", fontSize:10.5, padding:0 }}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button onClick={()=>setConfirmSlug(n.note_slug)} title="Delete note"
                        style={{ background:"none", border:"none", cursor:"pointer", color:"var(--fc-text-hint)", fontSize:12, padding:2,
                          opacity: hoverSlug===n.note_slug ? 1 : 0.4, transition:"opacity 0.15s, color 0.15s" }}
                        onMouseEnter={e=>e.currentTarget.style.color="var(--fc-red)"}
                        onMouseLeave={e=>e.currentTarget.style.color="var(--fc-text-hint)"}>
                        <i className="fa-solid fa-trash-can" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}
